
import { defineComponent, computed, ref, SetupContext } from 'vue';
import TIcon from './../t-icon/index';
import TRanger from '../t-ranger/index.vue';
import TActionSheet from '../t-action-sheet/index.vue';
import { ITSelectOption } from './../../utils/types';

export default defineComponent({
  name: 'TSelect',
  components: { TIcon, TRanger, TActionSheet },
  props: {
    modelValue: {
      type: Number,
      default: 0,
    },
    hasBottomBorder: {
      type: Boolean,
      default: false,
    },
    hasArrow: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasTitle: {
      type: Boolean,
      default: true,
    },
    hasLabel: {
      type: Boolean,
      default: false,
    },
    iconName: {
      type: String,
      default: 'down',
    },
    placeholder: {
      type: String,
      default: '',
    },
    confirmTitle: {
      type: String,
      default: '',
    },
    cancelTitle: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    textAlign: {
      type: String,
      default: 'center',
      validator: (value: string) => ['left', 'center', 'right'].includes(value),
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value: string) => ['xsmall', 'small', 'medium', 'large'].includes(value),
    },
  },
  emits: ['click', 'update:modelValue', 'change'],
  setup(props, context: SetupContext) {
    /** 组件的className前辍 */
    const prefixCls = ref('t-select');
    const modelValueBySelected = ref(0);
    const isActionSheetShow = ref(false);
    /** 组件的className前辍 */
    const getClasses = computed(() => {
      return {
        wrap: [prefixCls.value],
        body: [`${prefixCls.value}-bd`, !context.slots.default && `${prefixCls.value}-bd__default`],
        content: [`${prefixCls.value}-content`, `${prefixCls.value}-content__${props.textAlign}`],
        placeholder: [`${prefixCls.value}-placeholder`],
        arrowIcon: [`${prefixCls.value}-icon`],
        title: [`${prefixCls.value}-title`],
        label: [`${prefixCls.value}-label`],
        border: [
          `${prefixCls.value}-border`,
          props.hasBottomBorder && props.modelValue !== -1 && `${prefixCls.value}-border__edited`,
        ],
      };
    });
    /**
     * 判断是否有值
     * 如果值不合法，还需要将它重置为-1
     */
    const getIsNotEmptyValue = computed(() => props.modelValue < props.options.length && props.modelValue >= 0);
    /** 是否展示占位信息 */
    const getIsShowPlaceholder = computed(() => !getIsNotEmptyValue.value || !props.options.length);
    /** 是否展示头部标题信息 */
    const getIsShowTitle = computed(() => props.hasTitle && getIsNotEmptyValue.value && props.options.length);
    /** 是否展示提示信息 */
    const getIsShowLabel = computed(() => props.hasLabel && getIsNotEmptyValue.value && props.options.length);
    /** 是否展示标题 */
    const getTitle = computed(() => {
      return (getIsNotEmptyValue.value && (props.options[props.modelValue] as ITSelectOption).value) || '';
    });
    /** 是否展示描述信息 */
    const getLabel = computed(() => {
      return (getIsNotEmptyValue.value && (props.options[props.modelValue] as ITSelectOption).label) || '';
    });
    /**
     * 组件被点击，需要呼起actionsheet组件
     * 目前是直接写在模板里面
     */
    const onTap = () => {
      if (!props.disabled) {
        context.emit('click');
        modelValueBySelected.value = !props.modelValue || props.modelValue < 0 ? 0 : props.modelValue;
        isActionSheetShow.value = true;
      }
    };
    /**
     * 当确认的时候，需要更新select组件的value值（v-model）
     * 同时，因为modelValueBySelected的值，实际上是在ranger里面被处理掉了
     * 所以这里不需要再次更新modelValueBySelected的值
     */
    const onConfirm = () => {
      isActionSheetShow.value = false;
      const outputValue = (props.options[modelValueBySelected.value] as ITSelectOption)?.value;
      if (outputValue) {
        context.emit('update:modelValue', modelValueBySelected.value, outputValue);
        if (props.modelValue !== modelValueBySelected.value) {
          context.emit('change', modelValueBySelected.value, outputValue);
        }
      }
    };
    /**
     * 如果用户取消（包括点遮罩层）
     * 则需要手动恢复上一次选中的值（刚好value就是上一次变更的值）
     */
    const onCancel = () => {
      isActionSheetShow.value = false;
      const outputValue = (props.options[props.modelValue] as ITSelectOption)?.value;
      if (outputValue) {
        context.emit('update:modelValue', props.modelValue, outputValue);
        modelValueBySelected.value = props.modelValue;
      }
    };

    return {
      prefixCls,
      getClasses,
      modelValueBySelected,
      isActionSheetShow,
      getIsNotEmptyValue,
      getIsShowPlaceholder,
      getIsShowTitle,
      getIsShowLabel,
      getTitle,
      getLabel,
      onTap,
      onConfirm,
      onCancel,
    };
  },
});
